import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import type { Currency } from '@/api/common';
import { ScDepositSection } from '@/components/Deposit/styled';
import {
  amountWithCurrencySymbol,
  currencyFlags,
  mapCurrencyName,
} from '@/helpers/currency';
import { getCurrencyCentsAndCode } from '@/helpers/wallet';
import { usePlayerAccounts } from '@/modules/common/hooks/usePlayerAccounts';
import { useGetCommonStore } from '@/store/commonStore';
import { CurrencyItem } from './CurrencyItem';

export const CurrenciesAvailableBlock = () => {
  const useCommonStore = useGetCommonStore();
  const enableCurrencies = useCommonStore.use.enableCurrencies();
  const wallets = useCommonStore.use.wallets();

  const inactiveCurrenciesList = useMemo(() => {
    const inactiveCurrencies = wallets.map(({ currency }) => currency);
    return enableCurrencies.filter(
      ({ code }) => !inactiveCurrencies.includes(code)
    );
  }, [enableCurrencies, wallets]);

  return (
    <CurrenciesAvailableList inactiveCurrencies={inactiveCurrenciesList} />
  );
};

export const CurrenciesAvailableList: FC<{
  inactiveCurrencies: Currency[];
}> = ({ inactiveCurrencies }) => {
  const { t } = useTranslation('deposits');
  const useCommonStore = useGetCommonStore();

  const { enableCurrencies, pendingWallets } = useCommonStore(
    useShallow((state) => ({
      enableCurrencies: state.enableCurrencies,
      pendingWallets: state.pendingWallets,
    }))
  );

  const { data: allWallets } = usePlayerAccounts();

  const optionsWallets = useMemo(
    () =>
      inactiveCurrencies.map(({ code }) => {
        const name = mapCurrencyName[code];
        const balance =
          allWallets?.find(({ currency }) => currency === code)?.amountCents ||
          0;

        const { amount, currency } = getCurrencyCentsAndCode(
          balance,
          code,
          enableCurrencies
        );

        return {
          name: `${amountWithCurrencySymbol(
            currency,
            amount
          )}${name ? ` | ${name}` : ''}`,
          value: code,
          icon: currencyFlags[code],
        };
      }),
    [inactiveCurrencies, enableCurrencies, allWallets]
  );

  return (
    <ScDepositSection>
      <h3 className="title">{t('editCurrency.title')}</h3>

      <div className="list">
        {optionsWallets.map(({ value, name, icon }) => (
          <CurrencyItem
            currencyCode={value}
            disableButton={pendingWallets}
            icon={icon}
            key={value}
            label={name}
          />
        ))}
      </div>
    </ScDepositSection>
  );
};
